// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { AnimeProvider } from "./context/AnimeContext";
import { GlobalBgProvider } from "./context/GlobalBgContext";
import { ArtistProvider } from "./context/ArtistContext";
import { NotificationProvider } from "./context/NotificationContext";

/* Global UI */
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import ConstructionBanner from "./components/ConstructionBanner";
import TOSWrapper from "./components/TOSWrapper";

/* Watch Parties */
import WatchPartiesPage from "./pages/WatchPartiesPage";
import WatchPartyRoom from "./pages/WatchPartyRoom";
import StartWatchPartyPage from "./pages/StartWatchPartyPage";

/* Clubs pages */
import ClubsEntryPage from "./pages/Clubs/ClubsEntryPage";
import MyClubsPage from "./pages/Clubs/MyClubsPage";
import DiscoverClubsPage from "./pages/Clubs/ClubsDiscover";
import ClubHome from "./pages/Clubs/ClubHome";
import ClubPostThreadPage from "./pages/Clubs/ClubPostThreadPage";
import ClubCommentThreadPage from "./pages/Clubs/ClubCommentThreadPage";
import CreateClubPage from "./pages/Clubs/CreateClubPage";

/* Pages */
import Community from "./pages/Community";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AboutUs from "./pages/AboutUs";
import SearchResults from "./pages/SearchResults";
import AnimeDetails from "./pages/AnimeDetails";
import ProfileHub from "./pages/ProfileHub";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Feedback from "./pages/Feedback";
import HallOfFame from "./pages/HallOfFame";
import Discover from "./pages/Discover";
import InDevelopment from "./pages/InDevelopment";
import Favorites from "./pages/Favorites";
import Health from "./pages/Health";
import MessagesPage from "./pages/Messages/MessagesPage";

/* ArtHouse pages */
import ArtHouseFeedPage from "./pages/ArtHouse/ArtHouseFeedPage";
import ArtHouseGalleryPage from "./pages/ArtHouse/ArtHouseGalleryPage";
import ArtworksPage from "./pages/ArtHouse/ArtworksPage";
import ArtistProfilePage from "./pages/ArtHouse/ArtistProfilePage";

/* Dashboard pages */
import ArtistDashboardPage from "./pages/Dashboard/ArtistDashboardPage";
import NewArtworkPage from "./pages/Dashboard/NewArtworkPage";
import MyArtistProfilePage from "./pages/Dashboard/MyArtistProfilePage";
import EditArtworkPage from "./pages/Dashboard/EditArtworkPage";

/* MODAL OVERLAY */
import ArtistMiniCardModal from "./components/ArtHouse/ArtistMiniCardModal";
import NewArtworkModal from "./components/ArtHouse/NewArtworkModal";

/* NOTIFICATIONS */
import ToastContainer from "./components/ToastContainer";
import WatchPartyNotificationListener from "./components/WatchPartyNotificationListener";
import { CreateClubModalProvider } from "./components/Clubs/createClub/CreateClubModalContext";

/** Preserves query/hash for shared links that still use `/clubs/:slug/home`. */
function ClubSlugHomeRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/clubs/${encodeURIComponent(slug)}${location.search}${location.hash}`}
      replace
    />
  );
}

/* --------------------------------
   Protected Route wrapper
--------------------------------- */
function PrivateRoute({ children }) {
  const { authLoading, isAuthenticated } = useAuth();
  if (authLoading) return <p>Loading...</p>;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

/* --------------------------------
    AuthGate: ensures auth initializes before ANY page renders
--------------------------------- */
function AuthGate({ children }) {
  const { authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060812] text-white/70">
        Loading…
      </div>
    );
  }

  return children;
}

/* --------------------------------
   Scroll to top on route change
--------------------------------- */
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => window.scrollTo(0, 0), [location.pathname]);
  return null;
}

/* --------------------------------
   App shell (GLOBAL layout)
--------------------------------- */
function AppShell({ children }) {
  const location = useLocation();

  const hideNavbar =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password");

  return (
    <div className="min-h-screen bg-[#060812] text-white">
      <ConstructionBanner />
      <div className="pointer-events-none fixed inset-0">
        <div
          className="
            absolute inset-0 opacity-[0.45]
            [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)]
            [background-size:160px_160px]
            [background-position:12px_24px]
          "
        />
        <div
          className="
            absolute inset-0 opacity-[0.35]
            [background-image:radial-gradient(rgba(255,255,255,0.4)_1px,transparent_1px)]
            [background-size:110px_110px]
            [background-position:73px_41px]
          "
        />
        <div
          className="
            absolute inset-0 opacity-[0.25]
            [background-image:radial-gradient(rgba(255,255,255,0.9)_1px,transparent_1px)]
            [background-size:320px_320px]
            [background-position:180px_120px]
          "
        />
      </div>

      <div className="relative">
        {!hideNavbar && <Navbar />}

        {/* Full-bleed layout on auth pages so there is no empty left gutter */}
        <div
          className={[
            hideNavbar
              ? "min-h-screen w-full"
              : /* Match Navbar desktop aside width (w-[252px]) so main content clears the sidebar */
                "lg:pl-[252px] pb-[72px] lg:pb-0",
          ].join(" ")}
        >
          {children}
        </div>

      </div>
    </div>
  );
}

/* --------------------------------
   Routes wrapper (modal overlay support)
--------------------------------- */
function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/health" element={<Health />} />
        <Route path="/" element={<Discover />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/in-development" element={<InDevelopment />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/anime/:id" element={<AnimeDetails />} />
        <Route path="/hall-of-fame" element={<HallOfFame />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/messages" element={<MessagesPage />} />

        <Route path="/community" element={<Community />} />
        <Route path="/forum" element={<Community />} />
        <Route path="/community/thread/:threadId" element={<ClubPostThreadPage />} />
        <Route path="/thread/:postId" element={<ClubPostThreadPage />} />

        <Route path="/arthouse" element={<ArtHouseFeedPage />} />
        <Route path="/arthouse/gallery" element={<ArtHouseGalleryPage />} />
        <Route path="/arthouse/art/:artworkId" element={<ArtworksPage />} />
        <Route path="/arthouse/artist/:artistId" element={<ArtistProfilePage />} />

        {/* Clubs routes */}
        <Route path="/clubs" element={<ClubsEntryPage />} />
        <Route path="/clubs/my-clubs" element={<MyClubsPage />} />
        <Route path="/clubs/discover" element={<DiscoverClubsPage />} />
        <Route
          path="/clubs/new"
          element={
            <PrivateRoute>
              <CreateClubPage />
            </PrivateRoute>
          }
        />
        <Route path="/clubs/:slug/posts/:postId" element={<ClubPostThreadPage />} />
        <Route path="/clubs/:slug/posts/:postId/comments/:commentId/thread" element={<ClubCommentThreadPage />} />
        <Route path="/clubs/:slug/home" element={<ClubSlugHomeRedirect />} />
        <Route path="/clubs/:slug" element={<ClubHome />} />

        <Route
          path="/favorites"
          element={
            <PrivateRoute>
              <Favorites />
            </PrivateRoute>
          }
        />

        <Route path="/watch-parties" element={<WatchPartiesPage />} />
        <Route path="/watch-party/:id" element={<WatchPartyRoom />} />
        <Route
          path="/start-watch-party"
          element={
            <PrivateRoute>
              <StartWatchPartyPage />
            </PrivateRoute>
          }
        />

        {/* FIX: Make /hub/:userId render ProfileHub directly (public profile routing works) */}
        <Route path="/hub/:userId" element={<ProfileHub />} />

        {/* Keep /profilehub as your protected "my profile" entry point */}
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfileHub />
            </PrivateRoute>
          }
        />
        <Route
          path="/profilehub"
          element={
            <PrivateRoute>
              <ProfileHub />
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard/art"
          element={
            <PrivateRoute>
              <ArtistDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/art/new"
          element={
            <PrivateRoute>
              <NewArtworkPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/art/:artworkId/edit"
          element={
            <PrivateRoute>
              <EditArtworkPage />
            </PrivateRoute>
          }
        />

        <Route path="/dashboard" element={<Navigate to="/dashboard/profile" replace />} />
        <Route
          path="/dashboard/profile"
          element={
            <PrivateRoute>
              <MyArtistProfilePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/my-profile"
          element={
            <PrivateRoute>
              <MyArtistProfilePage />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      {backgroundLocation && (
        <Routes>
          <Route
            path="/arthouse/artist-modal/:artistId"
            element={<ArtistMiniCardModal />}
          />
          <Route
            path="/dashboard/art/new"
            element={
              <PrivateRoute>
                <NewArtworkModal />
              </PrivateRoute>
            }
          />
        </Routes>
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AnimeProvider>
        <GlobalBgProvider>
          <ArtistProvider>
            <NotificationProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <CreateClubModalProvider>
                  <ScrollToTop />

                  {/* Global listeners/UI that should exist regardless of route */}
                  <WatchPartyNotificationListener />
                  <ToastContainer />

                  {/* Gate rendering until auth finishes initializing */}
                  <AuthGate>
                    <AppShell>
                      <TOSWrapper>
                        <AppRoutes />
                        <Footer />
                        <CookieConsent />
                      </TOSWrapper>
                    </AppShell>
                  </AuthGate>
                </CreateClubModalProvider>
              </Router>
            </NotificationProvider>
          </ArtistProvider>
        </GlobalBgProvider>
      </AnimeProvider>
    </AuthProvider>
  );
}